
import CarouselDotsTemplate from "~/components/global/templates/CarouselDotsTemplate";

export default {
    spacingDebug: false,
    components: {
        CarouselDotsTemplate,
        HeaderTemplate: () => import("~/components/global/templates/HeaderTemplate"),
    },
    props: {
        loading: { type: Boolean, default: false },
        sectionId: {
            type: String,
            default: "carousel",
        },
        payload: {
            type: Array,
            default: () => [],
        },
        paginationPosition: {
            type: String,
            default: "right",
            validator: (value) => ["left", "center", "right", "bottom"].includes(value),
        },
        interval: {
            type: Number,
            default: 5000,
        },
        transition: {
            type: String,
            default: "fade",
            validator: (value) => ["slide", "fade"].includes(value),
        },
        theme: {
            type: String,
            default: "dark",
        },
        ariaLabel: {
            type: String,
            default: "Banner slides",
        },
    },
    data: () => ({
        requestedSlides: [0],
        activeIndex: 0,
        isPausedByUser: false,
        touchStartX: 0,
        touchStartY: 0,
        touchEndX: 0,
        touchEndY: 0,
    }),
    computed: {
        isLoading() {
            // SSR content must paint without waiting for client image events.
            return this.loading || !this.payload.length;
        },
        firstHeadingIndex() {
            return this.payload.findIndex((item) => item.title);
        },
        trackStyle() {
            if (this.transition === "fade") {
                return {
                    transform: "none",
                };
            }
            return {
                transform: `translate3d(-${this.activeIndex * 100}%,0,0)`,
            };
        },
        carouselStyle() {
            return {
                "--carousel-duration": `${this.interval}ms`,
            };
        },
        canAutoplay() {
            return this.interval > 0 && this.payload.length > 1;
        },
    },
    watch: {
        payload() {
            this.activeIndex = 0;
            this.requestedSlides = [0];
        },
    },
    methods: {
        handleCTA(event) {
            const slide = this.payload[this.activeIndex];
            if (!slide) return;
            if (slide.is_anchor) {
                event.preventDefault();
                const nav = document.querySelector(".__n");
                const navHeight = nav ? nav.offsetHeight : 0;
                this.$scrollTo(slide.link, {
                    offset: -navHeight,
                });
            }
        },
        resolveLink(url, isLinkOut) {
            if (!url) return "/";
            if (isLinkOut || url.startsWith("/") || url.startsWith("#")) {
                return url;
            }
            try {
                const { pathname, search, hash } = new URL(url);
                return `${pathname}${search}${hash}`;
            } catch (error) {
                return `/${url.replace(/^\/+/, "")}`;
            }
        },
        getImage(item, category) {
            if (!item || !Array.isArray(item.images)) {
                return "";
            }

            const image = item.images.find((i) => i?.category === category);

            return typeof image?.path === "string" ? image.path.trim() : "";
        },

        getImageAlt(item) {
            if (!item) {
                return "";
            }

            if (!Array.isArray(item.images)) {
                return typeof item.title === "string" ? item.title.trim() : "";
            }

            const image = item.images.find((i) => i?.category === "image");

            if (typeof image?.alt === "string" && image.alt.trim()) {
                return image.alt.trim();
            }

            return typeof item.title === "string" ? item.title.trim() : "";
        },
        goToNextSlide(index) {
            const total = this.payload.length;
            if (!total || this.isLoading) return;
            if (index < 0) {
                index = total - 1;
            }
            if (index >= total) {
                index = 0;
            }
            if (!this.requestedSlides.includes(index)) this.requestedSlides.push(index);
            this.activeIndex = index;
        },
        next() {
            this.goToNextSlide(this.activeIndex + 1);
        },
        previous() {
            this.goToNextSlide(this.activeIndex - 1);
        },
        toggleAutoplay() {
            this.isPausedByUser = !this.isPausedByUser;
        },
        onTouchStart(event) {
            if (this.payload.length < 2) return;
            if (event.touches.length !== 1) {
                return;
            }
            const touch = event.touches[0];
            this.touchStartX = touch.clientX;
            this.touchStartY = touch.clientY;
            this.touchEndX = touch.clientX;
            this.touchEndY = touch.clientY;
        },
        onTouchMove(event) {
            if (this.payload.length < 2) return;
            const touch = event.touches[0];
            this.touchEndX = touch.clientX;
            this.touchEndY = touch.clientY;
        },
        onTouchEnd() {
            if (this.payload.length < 2) return;
            const deltaX = this.touchEndX - this.touchStartX;
            const deltaY = this.touchEndY - this.touchStartY;
            if (Math.abs(deltaX) < 50) {
                return;
            }
            if (Math.abs(deltaY) > Math.abs(deltaX)) {
                return;
            }
            if (deltaX < 0) {
                this.next();
            } else {
                this.previous();
            }
        },
    },
};
